import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import UltimatePagination from 'react-ultimate-pagination-bootstrap-3';
import { Panel } from 'react-bootstrap';
import { orderBy, get } from 'lodash';

import { isBlank } from 'helpers/string_helpers';
import LoadingSpinner from 'components/loading_spinner';
import EmptyState from 'components/project/empty_state';
import ContactRolePropTypes from 'bundles/project/prop_types/contact_roles';
import ContactSelector from 'components/project/contact_selector';
import PeoplePageHeader from './people_page_header';
import PeopleFilterTopbar from './people_filter_topbar';
import LoadErrorMessage from 'components/project/load_error_message';
import PeopleTable from './people_table';
import { NAME, COLUMN_DEFINITIONS, CONFIGURABLE_COLUMNS } from './person_cell';

import '../styles.scss';

import { addContact, removeContact, updateContactRole } from 'components/project/actions';
import { retrievePeople } from '../../../actions/contacts';
import { setCurrentPersonColumns } from '../../../actions/session';
import { getProject } from '../../../selectors';
import {
  getPeople,
  getLoadError,
  getPersonColumns,
  isLoading
} from '../../../selectors/contacts';

export const RESULTS_PER_PAGE = 12;

export function PeopleTab({
  availableRelationships,
  currentColumns,
  isLoading,
  loadError,
  people,
  project,
  retrieveRecords,
  setColumns,
  onAddContact,
  onRemoveContact,
  onUpdateContact
}) {
  const [filters, setFilters] = useState({ searchText: '', relationshipFilter: null });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ field: NAME, reverse: true });
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState();

  useEffect(() => {
    retrieveRecords();
  }, []);

  const showContactEditModal = (contact = null) => {
    setShowContactModal(true);
    setSelectedContact(contact);
  };

  const hideContactEditModal = () => {
    setShowContactModal(false);
    setSelectedContact(null);
  };

  const handleFilterChange = filter => setFilters({ ...filters, ...filter });
  const changeSort = (fieldClicked) => {
    const reverse = sort.field === fieldClicked ? !sort.reverse : false;
    setSort({ field: fieldClicked, reverse });
  };

  const sortedContacts = () => {
    const { sortValue } = COLUMN_DEFINITIONS[sort.field];
    const direction = sort.reverse ? 'desc' : 'asc';
    return orderBy(people, [person => get(person, sortValue)], [direction]);
  };

  const searchFilter = (people) => {
    const filterText = (filters.searchText || '').toLowerCase();

    return filterText.length > 0
      ? people.filter(person => get(person, 'contactName').toLowerCase().includes(filterText))
      : people;
  };

  const relationshipOptions = () => [{ id: '', label: 'All Relationships', value: '' }, ...availableRelationships];
  const currentRelationship = () => availableRelationships.find(cr => cr.id === filters.relationshipFilter);

  const relationshipSearch = (relationship) =>  {
    if (relationship) setPage(1);

    handleFilterChange({ relationshipFilter: isBlank(relationship) ? null : relationship.id });
  };

  const relationshipFilter = (people) => {
    const relationship = currentRelationship();

    return relationship ? people.filter(person => person.relationship === relationship.id) : people;
  };

  const shownPeople = () => {
    let people = sortedContacts();
    people = searchFilter(people);
    people = relationshipFilter(people);
    return people;
  };

  const currentPage = () => Math.min(page, totalPages());
  const totalPages = () => Math.ceil(shownPeople().length / RESULTS_PER_PAGE);

  const currentPeople = () => {
    const start = (currentPage() - 1) * RESULTS_PER_PAGE;
    const end = start + RESULTS_PER_PAGE;
    return shownPeople().slice(start, end);
  };

  const searchPeople = (e) =>  {
    if (e.target.value) setPage(1);

    handleFilterChange({ searchText: (e.target.value || '') });
  };

  const handleListChange = () => {
    hideContactEditModal();
    retrieveRecords();
  };

  const addNewContact = contact => (
    onAddContact(
      project.id,
      contact.associatedModelId,
      contact.associatedModelType,
      contact.relation
    ).then(handleListChange)
  );

  const updateContact = contact => (
    onUpdateContact(
      project.id,
      selectedContact.id,
      contact.relation
    ).then(handleListChange)
  );

  const removeContact = contact => (
    onRemoveContact(
      project.id,
      contact.id
    ).then(() => retrieveRecords())
  );

  const renderEmptyState = () => (
    <EmptyState
      imageSrc="/images/project_empty_states/contacts.svg"
      message={people.length === 0 ? 'No contacts to display.' : 'No contacts match the current search criteria.'}
      subMessage={people.length === 0 ? 'Get started by clicking on the “New Contact” button.' : null}
    />
  );

  const renderTable = () => {
    return (
      <div className="border">
        <PeopleTable
          columns={currentColumns || CONFIGURABLE_COLUMNS}
          defaultColumns={CONFIGURABLE_COLUMNS}
          people={currentPeople()}
          sortColumn={sort.field}
          sortDesc={sort.reverse}
          stickyColumns={1}
          onRemoveContact={removeContact}
          onSetColumns={setColumns}
          onSort={changeSort}
          onUpdateContact={showContactEditModal}
        />
      </div>
    );
  };

  const renderPagination = () => {
    if (isLoading || totalPages() <= 1)  {
      return <div className="py-5"/>;
    }

    return (
      <div className="text-center">
        <UltimatePagination
          currentPage={currentPage()}
          totalPages={totalPages()}
          onChange={setPage}
        />
      </div>
    );
  };

  const renderBody = () => {
    let content;

    if (isLoading) {
      content = <LoadingSpinner addBottomBuffer />;
    } else if (shownPeople().length === 0) {
      content = renderEmptyState();
    } else {
      content = renderTable();
    }

    return (
      <Panel>
        <Panel.Body className="p-0">
          {content}
        </Panel.Body>
      </Panel>
    );
  };

  return (
    <React.Fragment>
      <PeoplePageHeader onAddContact={showContactEditModal} />
      <LoadErrorMessage loadError={loadError}/>
      <PeopleFilterTopbar
        currentPage={currentPage()}
        isLoading={isLoading}
        pageSize={RESULTS_PER_PAGE}
        personCount={currentPeople().length}
        relationshipFilter={currentRelationship()}
        relationshipOptions={relationshipOptions()}
        searchText={filters.searchText}
        totalCount={shownPeople().length}
        onRelationshipChange={relationshipSearch}
        onSearchChange={searchPeople}
      />
      {renderBody()}
      {renderPagination()}
      <ContactSelector
        personOrCompany="person"
        selectedContactRole={selectedContact}
        show={showContactModal}
        onCancel={hideContactEditModal}
        onSave={selectedContact ? updateContact : addNewContact}
      />      
    </React.Fragment>
  );
}

PeopleTab.propTypes = {
  availableRelationships: ContactRolePropTypes,
  currentColumns: PropTypes.arrayOf(PropTypes.string),
  isLoading: PropTypes.bool,
  loadError: PropTypes.string,
  people: PropTypes.array.isRequired,
  project: PropTypes.object.isRequired,
  retrieveRecords: PropTypes.func.isRequired,
  setColumns: PropTypes.func.isRequired,
  onAddContact: PropTypes.func.isRequired,
  onRemoveContact: PropTypes.func.isRequired,
  onUpdateContact: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  availableRelationships: state.contactRoles,
  currentColumns: getPersonColumns(state),
  isLoading: isLoading(state),
  loadError: getLoadError(state),
  people: getPeople(state),
  project: getProject(state)
});

const mapDispatchToProps = {
  onAddContact: addContact,
  onRemoveContact: removeContact,
  onUpdateContact: updateContactRole,
  retrieveRecords: retrievePeople,
  setColumns: setCurrentPersonColumns  
};

export default connect(mapStateToProps, mapDispatchToProps)(PeopleTab);
